import React, { useState, useEffect } from "react";
import axios from "axios";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import AsyncSelect from "react-select/async";
import { Box, Button, Modal, Typography, FormControlLabel, Checkbox } from "@mui/material";
import TextField from "@mui/material/TextField";
import { api } from "../../../utils/api";
import { showSuccessToast, showErrorToast } from "../../../utils/toast";
import { isCoffeePassProduct } from "../../../utils/coffeePass";

// Helper function to validate decimal places (avoids floating point precision issues)
const validateDecimalPlaces = (value, maxDecimals = 2) => {
  if (value === undefined || value === null || value === "") return true;
  // Convert to string to check decimal places accurately
  const valueStr = value.toString();
  const decimalIndex = valueStr.indexOf(".");
  if (decimalIndex === -1) return true; // No decimal point, valid
  const decimalPlaces = valueStr.length - decimalIndex - 1;
  return decimalPlaces <= maxDecimals;
};

const validationSchema = Yup.object().shape({
  // merchant: Yup.object().required("Merchant is required"),
  // product: Yup.object().required("Product is required"),
  price: Yup.number()
    .typeError("Price must be a valid number")
    .required("Price is required")
    .min(0, "Price must be greater than or equal to 0")
    .test("decimal", "Price can have at most 2 decimal places", (value) => validateDecimalPlaces(value, 2)),
  vehicleTypeSedan: Yup.number()
    .nullable()
    .typeError("Sedan/Hatch price must be a valid number")
    .min(0, "Sedan/Hatch price must be greater than or equal to 0")
    .test("decimal", "Sedan/Hatch price can have at most 2 decimal places", (value) => validateDecimalPlaces(value, 2)),
  vehicleTypeWagon: Yup.number()
    .nullable()
    .typeError("SUV/Wagon price must be a valid number")
    .min(0, "SUV/Wagon price must be greater than or equal to 0")
    .test("decimal", "SUV/Wagon price can have at most 2 decimal places", (value) => validateDecimalPlaces(value, 2)),
  vehicleType4wd: Yup.number()
    .nullable()
    .typeError("4WD/7Seater price must be a valid number")
    .min(0, "4WD/7Seater price must be greater than or equal to 0")
    .test("decimal", "4WD/7Seater price can have at most 2 decimal places", (value) => validateDecimalPlaces(value, 2))
});

const MerchantStorePricingForm = ({
  selectedMerchantStorePricing,
  callback,
  cancelModal,
  onlyStore,
  onlyAddOnProducts
}) => {
  // console.log(selectedMerchantStorePricing);
  const [modalActive, setModalActive] = useState(true);
  const [merchantOptions, setMerchantOptions] = useState([]);
  const [productOptions, setProductOptions] = useState([]);
  const [merchantStore, setMerchantStore] = useState("");
  const [product, setProduct] = useState("");

  // Debug: Log props
  useEffect(() => {
    console.log("🔧 MerchantStorePricingForm props:", {
      onlyAddOnProducts,
      onlyStore,
      hasSelectedPricing: !!selectedMerchantStorePricing
    });
  }, [onlyAddOnProducts, onlyStore, selectedMerchantStorePricing]);

  const getAllMerchantStores = async () => {
    try {
      console.log("SELECTING: ", onlyStore);
      // Include stores with new signups disabled (same as Store admin list); otherwise preset + search fail for those locations
      const response = await api("v1/merchantStore?includeSignupDisabled=true", "GET", null);
      if (response.data.success) {
        const allMerchantStores = response.data.data.allMerchantStores;
        const result = allMerchantStores.map((merchant) => ({
          value: merchant._id,
          label: merchant.name
        }));

        if (result) {
          setMerchantOptions(result);
          if (selectedMerchantStorePricing) {
            // Handle both cases: merchantStore as object with _id or as string ID
            const merchantStoreId =
              selectedMerchantStorePricing?.merchantStore?._id || selectedMerchantStorePricing?.merchantStore;
            const selectedMerchant = result.find(
              (merchant) =>
                merchant.value === merchantStoreId || merchant.value.toString() === merchantStoreId?.toString()
            );
            if (selectedMerchant) {
              setMerchantStore(selectedMerchant);
            }
          } else if (onlyStore) {
            const selectedMerchantStore = result.find(
              (merchant) => String(merchant.value) === String(onlyStore)
            );
            if (selectedMerchantStore) {
              setMerchantStore(selectedMerchantStore);
            }
          }
        }
      }
    } catch (error) {
      console.error("Error loading merchants:", error);
    }
  };

  const getAllProducts = async () => {
    try {
      // Use includeAll=true to get all products including add-on products
      const response = await api("v1/product?includeAll=true", "GET", null);
      if (response.data.success) {
        let allProducts = response.data.data.allProducts;

        console.log("📦 All products fetched:", allProducts.length);
        console.log("📦 Add-on products count:", allProducts.filter((p) => p.isAddOnOnly === true).length);

        // If editing, get the product being edited to ensure it's included
        let editingProductId = null;
        if (selectedMerchantStorePricing) {
          editingProductId = selectedMerchantStorePricing.product?._id || selectedMerchantStorePricing.product;
        }

        // Filter to only add-on products if onlyAddOnProducts prop is true
        if (onlyAddOnProducts) {
          const beforeFilter = allProducts.length;
          allProducts = allProducts.filter((product) => {
            // Always include the product being edited, even if it doesn't match the filter
            if (
              editingProductId &&
              (product._id.toString() === editingProductId.toString() || product._id === editingProductId)
            ) {
              return true;
            }
            return product.isAddOnOnly === true;
          });
          console.log(`📦 Filtered to add-on products: ${beforeFilter} -> ${allProducts.length}`);
        } else {
          // When not filtering, show ALL products (including add-ons)
          console.log("📦 Showing all products (including add-ons):", allProducts.length);
        }

        const result = allProducts.map((product) => ({
          value: product._id,
          label: product.name,
          isAddOnOnly: product.isAddOnOnly || false,
          supportsVehicleVariantPricing: product.supportsVehicleVariantPricing === true,
          vertical: product.vertical || "car_wash"
        }));

        if (result) {
          console.log("📦 Product options set:", result.length);
          setProductOptions(result);
          if (selectedMerchantStorePricing && editingProductId) {
            // Handle both cases: product as object with _id or as string ID
            const selectedProduct = result.find(
              (product) =>
                product.value.toString() === editingProductId.toString() || product.value === editingProductId
            );
            if (selectedProduct) {
              setProduct(selectedProduct);
            }
          }
        }
      } else {
        console.error("❌ Failed to fetch products:", response.data);
      }
    } catch (error) {
      console.error("❌ Error loading products:", error);
    }
  };

  useEffect(() => {
    getAllMerchantStores();
    getAllProducts();
  }, [onlyAddOnProducts, onlyStore]); // Re-fetch when modal context (store lock) or product filter changes

  const handleSubmit = async (values, { setSubmitting }) => {
    console.log("Submitted Data", values);
    const dataToSend = {
      merchantStore: merchantStore.value,
      product: product.value,
      price: values.price,
      adminFee: values.adminFee,
      priceFieldEdited: true,
      variantsFieldEdited: !!values.useVehicleVariantPricing,
    };

    const useVehicleVariantPricing = !isCoffeePassProduct(product) && !!values.useVehicleVariantPricing;
    dataToSend.useVehicleVariantPricing = useVehicleVariantPricing;
    if (useVehicleVariantPricing) {
      dataToSend.vehicleTypeVariants = {
        sedan: values.vehicleTypeSedan ?? null,
        wagon: values.vehicleTypeWagon ?? null,
        "4wd": values.vehicleType4wd ?? null,
      };
    } else {
      dataToSend.vehicleTypeVariants = {
        sedan: null,
        wagon: null,
        "4wd": null
      };
    }
    console.log("Data to Send", dataToSend);
    if (Object.keys(merchantStore).length == 0 || Object.keys(product).length == 0) {
      // Having issue with YUP validation will use this method for now.
      showErrorToast("Required Fields not selected");
      setSubmitting(false);
      return false;
    }
    try {
      let response;
      if (selectedMerchantStorePricing) {
        // Edit MerchantStorePricing Option
        response = await api(`v1/merchantStorePricing/${selectedMerchantStorePricing._id}`, "PATCH", dataToSend);
      } else {
        // Add MerchantStorePricing Option
        response = await api(`v1/merchantStorePricing`, "POST", dataToSend);
      }

      if (response.data.success) {
        if (selectedMerchantStorePricing) {
          const updated = response.data.data.updatedMerchantStorePricing;
          // PATCH returns populated merchantStore + product; do not interpolate objects into URLs (becomes "[object Object]")
          callback(updated);
          const productName =
            (typeof updated?.product === "object" && updated.product?.name) ||
            product?.label ||
            "product";
          showSuccessToast(`${productName} pricing updated successfully!`);
        } else {
          if (response.data.data.newMerchantStorePricing) {
            callback(response.data.data.newMerchantStorePricing);
            // Get product name from the selected product in the form or from the response
            const productName =
              product?.label || response.data.data.newMerchantStorePricing?.product?.name || "product";
            showSuccessToast(`${productName} pricing added successfully!`);
          } else {
            showErrorToast(response.data.message || "Failed to add pricing.");
            setSubmitting(false);
            return;
          }
        }
        // Close modal on success
        setTimeout(() => {
          if (cancelModal) {
            cancelModal();
          }
        }, 500);
      } else {
        showErrorToast(response?.data?.message || "Failed to save pricing.");
      }
    } catch (error) {
      console.error("API error:", error);
      showErrorToast(error.response?.data?.message || "An error occurred while saving pricing.");
    }

    setSubmitting(false);
  };

  const loadProductOptions = async (inputValue) => {
    try {
      const filteredOptions = productOptions.filter((option) =>
        option.label.toLowerCase().includes(inputValue.toLowerCase())
      );
      // callback(filteredOptions);
      return filteredOptions;
    } catch (error) {
      console.error("Error loading product options:", error);
      callback([]);
    }
  };

  const handleProductChange = (selectedOptions) => {
    if (selectedOptions) {
      setProduct(selectedOptions);
    } else {
      setProduct(null);
    }
  };

  const loadMerchantOptions = async (inputValue) => {
    try {
      const filteredOptions = merchantOptions.filter((option) =>
        option.label.toLowerCase().includes(inputValue.toLowerCase())
      );
      // callback(filteredOptions);
      return filteredOptions;
    } catch (error) {
      console.error("Error loading merchant options:", error);
      callback([]);
    }
  };

  const handleMerchantChange = (selectedOptions) => {
    if (selectedOptions) {
      setMerchantStore(selectedOptions);
    } else {
      setMerchantStore(null);
    }
  };

  const selectStyles = {
    control: (provided) => ({
      ...provided,
      backgroundColor: "white",
      borderColor: "#9e9e9e",
      zIndex: 2
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 3
    })
  };

  console.log({
    price: selectedMerchantStorePricing ? selectedMerchantStorePricing.price : "",
    merchantStore: merchantStore,
    product: product
  });

  // Set initial values for vehicle type variants
  const getInitialVehicleTypeVariants = () => {
    if (selectedMerchantStorePricing?.vehicleTypeVariants) {
      return {
        vehicleTypeSedan: selectedMerchantStorePricing.vehicleTypeVariants.sedan ?? "",
        vehicleTypeWagon: selectedMerchantStorePricing.vehicleTypeVariants.wagon ?? "",
        vehicleType4wd: selectedMerchantStorePricing.vehicleTypeVariants["4wd"] ?? "",
      };
    }
    return {
      vehicleTypeSedan: "",
      vehicleTypeWagon: "",
      vehicleType4wd: ""
    };
  };

  return (
    <Formik
      initialValues={{
        price: selectedMerchantStorePricing ? selectedMerchantStorePricing.price : 0,
        adminFee: selectedMerchantStorePricing ? selectedMerchantStorePricing.adminFee : 8,
        merchantStore: selectedMerchantStorePricing ? selectedMerchantStorePricing.merchantStore : "",
        product: selectedMerchantStorePricing ? selectedMerchantStorePricing.product : "",
        // Only explicit true checks the box. (Legacy rows often have null — do not treat as "on".)
        useVehicleVariantPricing:
          selectedMerchantStorePricing?.useVehicleVariantPricing === true,
        ...getInitialVehicleTypeVariants()
      }}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize={true}
    >
      {({ values }) => (
      <Form className="form w-full flex flex-column justify-between priorityoneform min-h-[20vh]">
        <div className="form-input-wrap flex gap-2 w-full mb-2">
          <div className="w-full flex gap-5">
            <div className="form-group w-full">
              <label className="label" htmlFor="merchantStore">
                Store Name:
              </label>
              {/* <Field name="merchantStore" className="z-2">
                                {({ field }) => (
                                    <AsyncSelect
                                        name="merchantStore"
                                        {...field}
                                        cacheOptions
                                        defaultOptions
                                        loadOptions={loadMerchantOptions}
                                        styles={selectStyles}
                                        onChange={handleMerchantChange}
                                        value={merchantStore}
                                    />
                                )}
                            </Field> */}
              <AsyncSelect
                name="merchantStore"
                cacheOptions
                defaultOptions={merchantOptions}
                loadOptions={loadMerchantOptions}
                styles={selectStyles}
                onChange={handleMerchantChange}
                value={merchantStore}
                isDisabled={!!onlyStore && !selectedMerchantStorePricing}
                placeholder={
                  onlyStore && !merchantStore ? "Loading store…" : "Select store…"
                }
              />
              <ErrorMessage name="merchantStore" component="span" className="error-message" />
            </div>
          </div>
          <div className="w-full flex gap-5">
            <div className="form-group w-full">
              <label className="label" htmlFor="product">
                Product:
              </label>
              {/* <Field name="product" className="z-2">
                                {({ field }) => (
                                    <AsyncSelect
                                        name="product"
                                        {...field}
                                        cacheOptions
                                        defaultOptions
                                        loadOptions={loadProductOptions}
                                        styles={selectStyles}
                                        onChange={handleProductChange}
                                        value={product}
                                    />
                                )}
                            </Field> */}
              <Field name="product">
                {({ field }) => (
                  <AsyncSelect
                    name="product"
                    {...field}
                    cacheOptions
                    defaultOptions={productOptions}
                    loadOptions={loadProductOptions}
                    styles={selectStyles}
                    onChange={handleProductChange}
                    value={product}
                  />
                )}
              </Field>
              <ErrorMessage name="product" component="span" className="error-message" />
            </div>
          </div>
          <div className="w-full flex gap-5">
            <div className="form-group w-full">
              {/* <label htmlFor="price">Price:</label> */}
              <Field name="price">
                {({ field }) => (
                  <TextField
                    {...field}
                    id="price"
                    type="number"
                    label="Price (Including GST)"
                    className="z-2"
                    inputProps={{ min: 0, step: 0.01 }}
                    placeholder="Enter price (e.g., 99.99)"
                    helperText="Enter price with up to 2 decimal places"
                  />
                )}
              </Field>
              <ErrorMessage name="price" component="span" className="error-message" />
            </div>
          </div>
          {/* Admin Fee */}
          <div className="w-full flex gap-5">
            <div className="form-group w-full">
              {/* <label htmlFor="adminFee">Admin Fee:</label> */}
              <Field name="adminFee">
                {({ field }) => (
                  <TextField
                    {...field}
                    id="adminFee"
                    type="number"
                    label="Admin Fee (%)"
                    className="z-2"
                    inputProps={{ min: 0, step: 0.01 }}
                    placeholder="Enter admin fee (e.g., 5.00)"
                    helperText="Enter admin fee with up to 2 decimal places"
                  />
                )}
              </Field>
              <ErrorMessage name="adminFee" component="span" className="error-message" />
            </div>
          </div>

          {product && !isCoffeePassProduct(product) && (
            <div className="w-full flex gap-5">
              <div className="form-group w-full">
                <Field name="useVehicleVariantPricing">
                  {({ field, form }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!field.value}
                          onChange={(_, checked) => form.setFieldValue(field.name, checked)}
                        />
                      }
                      label="Use vehicle-size pricing (sedan / SUV / 4WD) for this product"
                    />
                  )}
                </Field>
                <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                  When off, only the base price above is used for this store row. The product must have
                  vehicle-size pricing enabled in the catalogue for tier amounts to affect billing.
                </Typography>
                {!product.supportsVehicleVariantPricing && (
                  <Typography variant="caption" display="block" color="warning.main" sx={{ mt: 0.5 }}>
                    Catalogue flag for this product is off — redemptions will bill from the base price
                    until it is enabled (e.g. migration script).
                  </Typography>
                )}
              </div>
            </div>
          )}

          {product && !isCoffeePassProduct(product) && values.useVehicleVariantPricing && (
            <>
              <div className="w-full mt-4 mb-2">
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Vehicle Type Variants
                </Typography>
              </div>
              <div className="w-full flex gap-5">
                <div className="form-group w-full">
                  <Field name="vehicleTypeSedan">
                    {({ field }) => (
                      <TextField
                        {...field}
                        id="vehicleTypeSedan"
                        type="number"
                        label="Sedan/Hatch Price (Including GST)"
                        className="z-2"
                        inputProps={{ min: 0, step: 0.01 }}
                        placeholder="Enter price for Sedan/Hatch (e.g., 99.99)"
                        helperText="Optional: Enter price for Sedan/Hatch variant"
                      />
                    )}
                  </Field>
                </div>
              </div>
              <div className="w-full flex gap-5">
                <div className="form-group w-full">
                  <Field name="vehicleTypeWagon">
                    {({ field }) => (
                      <TextField
                        {...field}
                        id="vehicleTypeWagon"
                        type="number"
                        label="SUV/Wagon Price (Including GST)"
                        className="z-2"
                        inputProps={{ min: 0, step: 0.01 }}
                        placeholder="Enter price for SUV/Wagon (e.g., 119.99)"
                        helperText="Optional: Enter price for SUV/Wagon variant"
                      />
                    )}
                  </Field>
                </div>
              </div>
              <div className="w-full flex gap-5">
                <div className="form-group w-full">
                  <Field name="vehicleType4wd">
                    {({ field }) => (
                      <TextField
                        {...field}
                        id="vehicleType4wd"
                        type="number"
                        label="4WD/7Seater Price (Including GST)"
                        className="z-2"
                        inputProps={{ min: 0, step: 0.01 }}
                        placeholder="Enter price for 4WD/7Seater (e.g., 139.99)"
                        helperText="Optional: Enter price for 4WD/7Seater variant"
                      />
                    )}
                  </Field>
                </div>
              </div>
            </>
          )}
        </div>
        <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
          <Button variant="contained" onClick={cancelModal}>
            Cancel
          </Button>
          <Button type="submit" variant="contained">
            Submit
          </Button>
        </Box>
      </Form>
      )}
    </Formik>
  );
};

export default MerchantStorePricingForm;
